const SortBy = require('lodash/sortBy');

const FORM_ID = 'user-search-filter-form';

const internals = {};

const FIELD_NAMES = {
    contextFilter: 'contextFilter',
    name: 'name',
    studentName: 'studentName',
    major: 'major',
    career: 'career',
    profession: 'profession',
    bio: 'bio',
    title: 'title',
    departmentId: 'departmentId',
    officeId: 'officeId',
    yearHiredId: 'yearHiredId',
    workRemote: 'workRemote',
    badge: 'badge',
    locationHeader: 'locationHeader',
    locationType: 'locationType',
    customLocation: 'customLocation',
    radius: 'radius',
    locationSpacer: 'locationSpacer',
    interestsHeader: 'interestsHeader',
    incomingClass: 'incomingClass',
    graduatingClass: 'graduatingClass',
    isTransfer: 'isTransfer',
    isOnline: 'isOnline',
    isVeteran: 'isVeteran',
    searchIsParent: 'searchIsParent',
    fullTimeStatus: 'fullTimeStatus',
    interests: 'interests'
};

const DEFAULT_FIELD_NAMES = [
    FIELD_NAMES.contextFilter,
    FIELD_NAMES.name,
    FIELD_NAMES.studentName,
    FIELD_NAMES.major,
    FIELD_NAMES.career,
    FIELD_NAMES.profession,
    FIELD_NAMES.bio,
    FIELD_NAMES.title,
    FIELD_NAMES.departmentId,
    FIELD_NAMES.officeId,
    FIELD_NAMES.yearHiredId,
    FIELD_NAMES.workRemote,
    FIELD_NAMES.badge,
    FIELD_NAMES.incomingClass,
    FIELD_NAMES.graduatingClass,
    FIELD_NAMES.isTransfer,
    FIELD_NAMES.isOnline,
    FIELD_NAMES.isVeteran,
    FIELD_NAMES.searchIsParent,
    FIELD_NAMES.fullTimeStatus,
    FIELD_NAMES.locationHeader,
    FIELD_NAMES.locationType,
    FIELD_NAMES.customLocation,
    FIELD_NAMES.radius,
    FIELD_NAMES.locationSpacer,
    FIELD_NAMES.interestsHeader,
    FIELD_NAMES.interests
];

// Admin search forms intentionally omit location-related fields so they
// cannot render or submit location criteria.
const ADMIN_FIELD_NAMES = [
    FIELD_NAMES.contextFilter,
    FIELD_NAMES.name,
    FIELD_NAMES.studentName,
    FIELD_NAMES.major,
    FIELD_NAMES.career,
    FIELD_NAMES.profession,
    FIELD_NAMES.bio,
    FIELD_NAMES.title,
    FIELD_NAMES.departmentId,
    FIELD_NAMES.officeId,
    FIELD_NAMES.yearHiredId,
    FIELD_NAMES.workRemote,
    FIELD_NAMES.badge,
    FIELD_NAMES.interestsHeader,
    FIELD_NAMES.incomingClass,
    FIELD_NAMES.graduatingClass,
    FIELD_NAMES.isTransfer,
    FIELD_NAMES.isOnline,
    FIELD_NAMES.isVeteran,
    FIELD_NAMES.searchIsParent,
    FIELD_NAMES.fullTimeStatus,
    FIELD_NAMES.interests
];

const FIELD_VALUE_NAMES = {
    [FIELD_NAMES.customLocation]: [FIELD_NAMES.customLocation, 'customLocationText']
};

module.exports.FORM_ID = FORM_ID;
module.exports.FIELD_NAMES = FIELD_NAMES;
module.exports.DEFAULT_FIELD_NAMES = DEFAULT_FIELD_NAMES;
module.exports.ADMIN_FIELD_NAMES = ADMIN_FIELD_NAMES;

module.exports.buildFormConfig = function buildFormConfig(props) {

    const {
        defaults,
        onSubmit
    } = props;

    const fieldNames = internals.resolveFieldNames(props.fieldNames);

    return {
        defaults,
        fields: internals.buildFields({ ...props, fieldNames }),
        formId: FORM_ID,
        onSubmit: async (values, ...args) => {

            const searchValues = internals.filterValuesByFieldNames(values, fieldNames);

            return onSubmit(searchValues, ...args);
        },
        syncDefaults: false
    };
};

internals.boolOptions = [
    { value: '', label: 'Any' },
    { value: true, label: 'Yes' },
    { value: false, label: 'No' }
];

internals.radiusOptions = [
    { value: '', label: 'Any' },
    { value: 0.5, label: '0.5 miles (0.8 km)' },
    { value: 1, label: '1 mile (1.6 km)' },
    { value: 5, label: '5 miles (8 km)' },
    { value: 10, label: '10 miles (16 km)' },
    { value: 15, label: '15 miles (24 km)' },
    { value: 20, label: '20 miles (32 km)' },
    { value: 50, label: '50 miles (80 km)' },
    { value: 100, label: '100 miles (160 km)' },
    { value: 500, label: '500 miles (804 km)' }
];

internals.asArray = (value) => Array.isArray(value) ? value : [];

internals.withAnyOption = (options = []) => ([
    { value: '', label: 'Any' },
    ...internals.asArray(options)
]);

internals.EmptySelectIcon = () => null;

internals.getRoleFieldTextFieldProps = ({
    fieldProps,
    roleOptions
}) => {

    const safeFieldProps = fieldProps || {};

    if (internals.asArray(roleOptions).length !== 1) {
        return safeFieldProps;
    }

    const selectProps = safeFieldProps.SelectProps || {};
    const sx = selectProps.sx || {};
    const selectSx = sx['& .MuiSelect-select'] || {};

    return {
        ...safeFieldProps,
        SelectProps: {
            ...selectProps,
            IconComponent: internals.EmptySelectIcon,
            sx: {
                ...sx,
                '& .MuiSelect-select': {
                    ...selectSx,
                    paddingRight: '14px !important'
                }
            }
        }
    };
};

internals.getLocationOptions = ({ canSearchHometown, canSearchHousing, includeCustomLocation, isCompany, isCommunity, isSchoolOnline }) => {

    const hometownLabel = (isCompany || isCommunity || isSchoolOnline) ? 'Where I Grew Up' : 'My Hometown';
    const housingLabel = 'Where I Live';

    return [
        { value: '', label: 'Any' },
        canSearchHousing && { value: 'housing', label: housingLabel },
        canSearchHometown && { value: 'hometown', label: hometownLabel },
        includeCustomLocation && { value: 'custom', label: 'Custom Location' }
    ].filter(Boolean);
};

internals.buildFields = (options = {}) => {

    const {
        canSearchHometown,
        canSearchHousing,
        currentUserPermissions,
        fieldNames = DEFAULT_FIELD_NAMES,
        fieldProps = {},
        forApplicability = false,
        graduatingClassList = [],
        incomingClassList = [],
        interestsList = [],
        isCompany,
        isCommunity,
        isSchoolOnline,
        majors = [],
        officesList = [],
        renderInterestsHeader,
        renderLocationHeader,
        roleContext = {},
        roleOptions = [],
        selectedLocationType,
        selectedRoleGroup,
        setSelectedLocationType,
        setSelectedRoleGroup,
        yearsHiredList = []
    } = options;

    const safeGraduatingClassList = internals.asArray(graduatingClassList);
    const safeIncomingClassList = internals.asArray(incomingClassList);
    const safeInterestsList = internals.asArray(interestsList);
    const safeMajors = internals.asArray(majors);
    const safeOfficesList = internals.asArray(officesList);
    const safeRoleOptions = internals.asArray(roleOptions);
    const safeYearsHiredList = internals.asArray(yearsHiredList);
    const resolvedRoleContext = {
        isParent: false,
        canEdit: () => false,
        ...roleContext,
        badges: internals.asArray(roleContext?.badges),
        departments: internals.asArray(roleContext?.departments)
    };
    const canView = (permission) => currentUserPermissions?.[permission] !== false;
    const canEdit = (permission) => resolvedRoleContext.canEdit(permission);
    const isParent = resolvedRoleContext.isParent;
    const includedFields = new Set(internals.resolveFieldNames(fieldNames));
    const locationOptions = internals.getLocationOptions({
        canSearchHometown,
        canSearchHousing,
        includeCustomLocation: includedFields.has(FIELD_NAMES.customLocation),
        isCompany,
        isCommunity,
        isSchoolOnline
    });
    const showLocationSection = selectedRoleGroup && canEdit('canEditHomeTown') && locationOptions.length > 1;
    const shouldShowIncomingClassField = safeIncomingClassList.length > 0 || forApplicability;
    const shouldShowGraduatingClassField = safeGraduatingClassList.length > 0 || forApplicability;
    const fieldIsIncluded = (field) => {

        const fieldName = field.fieldName || field.name || field.key;
        return fieldName && includedFields.has(fieldName);
    };

    return [
        safeRoleOptions.length > 0 && {
            type: 'SelectField',
            name: 'contextFilter',
            label: 'User Type',
            options: safeRoleOptions.map(({ name, label }) => ({
                value: name,
                label: label || name
            })),
            onChange: setSelectedRoleGroup,
            textFieldProps: internals.getRoleFieldTextFieldProps({
                fieldProps,
                roleOptions: safeRoleOptions
            }),
            fullWidth: true
        },
        selectedRoleGroup && {
            type: 'TextField',
            name: 'name',
            label: 'Name',
            clearable: true,
            textFieldProps: fieldProps
        },
        selectedRoleGroup && isParent && canView('canViewStudentName') && canEdit('canEditStudentName') && {
            type: 'TextField',
            name: 'studentName',
            label: 'Student Name',
            clearable: true,
            textFieldProps: fieldProps
        },
        selectedRoleGroup && !isParent && canView('canViewMajor') && canEdit('canEditMajor') && {
            type: 'SelectField',
            name: 'major',
            label: 'Major or Program',
            options: internals.withAnyOption(SortBy(safeMajors, 'name')),
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canView('canViewCareerAspiration') && canEdit('canEditCareerAspiration') && {
            type: 'TextField',
            name: 'career',
            label: 'Career Aspiration',
            clearable: true,
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canView('canViewProfession') && canEdit('canEditProfession') && {
            type: 'TextField',
            name: 'profession',
            label: 'Profession',
            clearable: true,
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canView('canViewBio') && canEdit('canEditBio') && {
            type: 'TextField',
            name: 'bio',
            label: 'Bio',
            clearable: true,
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canView('canViewTitle') && canEdit('canEditTitle') && {
            type: 'TextField',
            name: 'title',
            label: 'Title',
            clearable: true,
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canView('canViewDepartment') && canEdit('canEditDepartment') && {
            type: 'SelectField',
            name: 'departmentId',
            label: 'Department',
            options: internals.withAnyOption(SortBy(resolvedRoleContext.departments, 'name')),
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canView('canViewOffice') && canEdit('canEditOffice') && {
            type: 'SelectField',
            name: 'officeId',
            label: isCompany ? 'Affiliated Office' : 'Campus',
            options: internals.withAnyOption(SortBy(safeOfficesList, 'name')),
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canView('canViewYearHired') && canEdit('canEditYearHired') && {
            type: 'SelectField',
            name: 'yearHiredId',
            label: 'Year Hired',
            options: internals.withAnyOption(SortBy(safeYearsHiredList, 'year')),
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canView('canViewWorkRemote') && canEdit('canEditWorkRemote') && {
            type: 'SelectField',
            name: 'workRemote',
            label: 'Work Remote',
            options: internals.boolOptions,
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canView('canViewBadge') && resolvedRoleContext.badges.length > 0 && {
            type: 'SelectField',
            name: 'badge',
            label: 'Badge',
            options: internals.withAnyOption(resolvedRoleContext.badges.map(({ name, label }) => ({ value: name, label }))),
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canView('canViewIncomingClass') && canEdit('canEditIncomingClass') && shouldShowIncomingClassField && {
            type: 'SelectField',
            name: 'incomingClass',
            label: 'Incoming Class',
            options: internals.withAnyOption(safeIncomingClassList),
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canView('canViewGraduatingClass') && canEdit('canEditGraduatingClass') && shouldShowGraduatingClassField && {
            type: 'SelectField',
            name: 'graduatingClass',
            label: 'Graduating Class',
            options: internals.withAnyOption(safeGraduatingClassList),
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canEdit('canEditTransfer') && {
            type: 'SelectField',
            name: 'isTransfer',
            label: 'Transfer Student',
            options: internals.boolOptions,
            textFieldProps: fieldProps
        },
        selectedRoleGroup && !isSchoolOnline && canEdit('canEditOnline') && {
            type: 'SelectField',
            name: 'isOnline',
            label: 'Online Student',
            options: internals.boolOptions,
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canEdit('canEditVeteran') && {
            type: 'SelectField',
            name: 'isVeteran',
            label: 'Veteran',
            options: internals.boolOptions,
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canEdit('canEditParent') && {
            type: 'SelectField',
            name: 'searchIsParent',
            label: 'Parent',
            options: internals.boolOptions,
            textFieldProps: fieldProps
        },
        selectedRoleGroup && canEdit('canEditTimeStatus') && {
            type: 'SelectField',
            name: 'fullTimeStatus',
            label: 'Full / Part-time',
            options: [
                { value: '', label: 'Any' },
                { value: 'full-time', label: 'Full-time' },
                { value: 'part-time', label: 'Part-time' }
            ],
            textFieldProps: fieldProps
        },
        showLocationSection && {
            fieldName: FIELD_NAMES.locationHeader,
            type: 'custom',
            key: FIELD_NAMES.locationHeader,
            render: renderLocationHeader
        },
        showLocationSection && {
            type: 'SelectField',
            name: 'locationType',
            label: 'Location',
            options: locationOptions,
            onChange: setSelectedLocationType,
            textFieldProps: fieldProps,
            fullWidth: true
        },
        selectedRoleGroup && canEdit('canEditHomeTown') && selectedLocationType === 'custom' && {
            type: 'PlaceField',
            name: 'customLocation',
            label: 'Custom Location',
            textFieldName: 'customLocationText',
            clearValue: null,
            errorFieldName: 'customLocation',
            exactOptionMessage: 'Please select a valid custom location from the dropdown.',
            placeholder: 'Type to search...',
            rules: {
                validate: (value) => !!value?.placeId || 'Please select a valid custom location from the dropdown.'
            },
            textFieldProps: {
                ...fieldProps,
                inputProps: {
                    'aria-label': 'Custom Location'
                }
            }
        },
        selectedRoleGroup && canEdit('canEditHomeTown') && selectedLocationType && {
            type: 'SelectField',
            name: 'radius',
            label: 'Distance',
            options: internals.radiusOptions,
            textFieldProps: {
                ...fieldProps,
                id: 'radius-input'
            },
            fullWidth: true
        },
        showLocationSection && selectedLocationType && {
            fieldName: FIELD_NAMES.locationSpacer,
            type: 'spacer',
            height: 16
        },
        selectedRoleGroup && {
            fieldName: FIELD_NAMES.interestsHeader,
            type: 'custom',
            key: FIELD_NAMES.interestsHeader,
            render: renderInterestsHeader
        },
        selectedRoleGroup && {
            type: 'InterestsInputField',
            name: 'interests',
            label: 'Interests',
            options: SortBy(safeInterestsList, 'name'),
            interestsInputProps: {
                hideInstructions: true,
                hideSuggestInterest: true,
                interests: SortBy(safeInterestsList, 'name')
            }
        }
    ].filter(Boolean)
        .filter(fieldIsIncluded)
        .map(({ fieldName, ...field }) => field);
};

internals.resolveFieldNames = (fieldNames) => Array.isArray(fieldNames) && fieldNames.length ? fieldNames : DEFAULT_FIELD_NAMES;

internals.getFieldValueNames = (fieldName) => FIELD_VALUE_NAMES[fieldName] || [fieldName];

internals.filterValuesByFieldNames = (values = {}, fieldNames = DEFAULT_FIELD_NAMES) => {

    const allowedValueNames = new Set(internals.resolveFieldNames(fieldNames)
        .flatMap(internals.getFieldValueNames));

    return Object.keys(values).reduce((collector, key) => {

        if (allowedValueNames.has(key)) {
            collector[key] = values[key];
        }

        return collector;
    }, {});
};

module.exports.internals = internals;
